/** Expanded game detail card shown when a bar is tapped */

import { betOutcome } from '@parlay-picker/shared/lineOutcome';
import { STAT_SHORT_LABELS } from '@parlay-picker/shared/statsChart';
import { cn } from '@utils/cn';
import { motion } from 'framer-motion';
import type { ReactElement } from 'react';
import { OUTCOME_STYLE } from './outcomeStyles';
import type { ChartDataPoint } from './types';

interface GameDetailCardProps {
  point: ChartDataPoint;
  statKey: string;
  propLine: number;
  isUnderBet: boolean;
  isAlternateLine: boolean;
  onClose: () => void;
}

export function GameDetailCard({
  point,
  statKey,
  propLine,
  isUnderBet,
  isAlternateLine,
  onClose,
}: GameDetailCardProps): ReactElement {
  const shortLabel = STAT_SHORT_LABELS[statKey] || statKey;
  const outcome = point.dnp
    ? null
    : betOutcome(point.value, propLine, isUnderBet ? 'under' : 'over', isAlternateLine);

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className="overflow-hidden"
    >
      <div
        className={cn(
          'flex items-center justify-between rounded-lg px-3 py-2 mt-2 border cursor-pointer',
          outcome ? OUTCOME_STYLE[outcome].card : 'bg-theme-surface/60 border-theme-border',
        )}
        onClick={onClose}
      >
        <div className="flex items-center gap-3">
          <div>
            <div className="text-xs text-theme-text-tertiary">
              {point.dateFormatted || point.label}
            </div>
            {point.opponent && (
              <div className="text-xs text-theme-text-tertiary/70">vs {point.opponent}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!outcome ? (
            <span className="text-sm font-bold text-theme-text-tertiary">DNP</span>
          ) : (
            <>
              <span className={cn('text-sm font-bold', OUTCOME_STYLE[outcome].text)}>
                {point.value} {shortLabel}
              </span>
              {point.min && (
                <span className="text-[10px] text-theme-text-tertiary">{point.min} min</span>
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}
